import React, { useState, useEffect, useMemo, useCallback } from 'react';
import b2bService from '../../services/b2bService';
import masterService from '../../services/masterService';
import { useAuth } from '../../context/AuthContext';
import './B2bSpecimenLogisticsPage.css';

const B2bSpecimenLogisticsPage = () => {
  const { user } = useAuth() || {};

  // Manifests & Centres State
  const [manifests, setManifests] = useState([]);
  const [centres, setCentres] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedManifest, setSelectedManifest] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [scanBarcode, setScanBarcode] = useState('');
  const [addingItem, setAddingItem] = useState(false);

  // Search & Filter State
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');

  // Create Manifest Modal State
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [isSubmittingCreate, setIsSubmittingCreate] = useState(false);
  const [sourceCentreId, setSourceCentreId] = useState('');
  const [destinationCentreId, setDestinationCentreId] = useState('');
  const [courierName, setCourierName] = useState('');
  const [courierTrackingRef, setCourierTrackingRef] = useState('');
  const [bagSealNumber, setBagSealNumber] = useState('');
  const [dispatchTemp, setDispatchTemp] = useState('4.0');
  const [manifestNotes, setManifestNotes] = useState('');

  // Dispatch & Receive Action Dialog State
  const [dispatchDialogManifestId, setDispatchDialogManifestId] = useState(null);
  const [dispatchModalTemp, setDispatchModalTemp] = useState('4.0');
  const [isDispatching, setIsDispatching] = useState(false);

  const [receiveDialogManifestId, setReceiveDialogManifestId] = useState(null);
  const [receiveModalTemp, setReceiveModalTemp] = useState('4.5');
  const [receiveModalNotes, setReceiveModalNotes] = useState('Specimens received intact at destination laboratory');
  const [isReceiving, setIsReceiving] = useState(false);

  // Help Guide Toggle State
  const [showGuide, setShowGuide] = useState(true);

  // Toast Notification State
  const [toast, setToast] = useState(null);

  const showToast = useCallback((message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => {
      setToast((prev) => (prev?.message === message ? null : prev));
    }, 4500);
  }, []);

  // Fetch Centres List for Dropdowns
  const fetchCentres = useCallback(async () => {
    try {
      const res = await masterService.getCentres({ isActive: true });
      const rawList = Array.isArray(res)
        ? res
        : Array.isArray(res?.data)
        ? res.data
        : Array.isArray(res?.data?.data)
        ? res.data.data
        : [];

      // Normalize all centre object properties regardless of casing
      const list = rawList.map((c) => ({
        centreId: c.centreId ?? c.CentreId ?? c.centreID ?? c.id,
        centreName: c.centreName ?? c.CentreName ?? c.name ?? `Centre ${c.centreId ?? c.id}`,
        centreCode: c.centreCode ?? c.CentreCode ?? c.code ?? '',
        isNabl: Boolean(c.isNabl ?? c.IsNabl),
        city: c.city ?? c.City ?? ''
      }));

      setCentres(list);

      // Pre-select sensible defaults
      if (list.length > 0) {
        const userCentre = Number(user?.centreId || user?.centre_id || 1);
        const defaultSource = list.find((c) => c.centreId === userCentre) || list[0];
        setSourceCentreId(String(defaultSource.centreId));

        const defaultDest = list.find((c) => c.centreId !== defaultSource.centreId) || list[1] || list[0];
        setDestinationCentreId(String(defaultDest.centreId));
      }
    } catch (err) {
      console.warn('Unable to load centres list:', err);
    }
  }, [user]);

  // Fetch Manifests List
  const fetchManifests = useCallback(async () => {
    setLoading(true);
    try {
      const data = await b2bService.getManifests();
      setManifests(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error fetching manifests:', err);
      showToast(err.response?.data?.message || 'Failed to load transfer manifests.', 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    fetchCentres();
    fetchManifests();
  }, [fetchCentres, fetchManifests]);

  // Load Manifest Details
  const handleSelectManifest = async (id) => {
    if (!id) return;
    setDetailsLoading(true);
    try {
      const data = await b2bService.getManifestDetails(id);
      setSelectedManifest(data);
    } catch (err) {
      showToast(err.response?.data?.message || 'Unable to load manifest details.', 'error');
    } finally {
      setDetailsLoading(false);
    }
  };

  // Create Manifest Handler
  const handleCreateManifest = async (e) => {
    e.preventDefault();
    if (!sourceCentreId || !destinationCentreId) {
      showToast('Please select both source and destination centres.', 'error');
      return;
    }
    if (sourceCentreId === destinationCentreId) {
      showToast('Source centre and destination processing lab must be different.', 'error');
      return;
    }

    setIsSubmittingCreate(true);
    try {
      const payload = {
        sourceCentreId: Number(sourceCentreId),
        destinationCentreId: Number(destinationCentreId),
        courierName: courierName.trim() || 'Direct Laboratory Courier',
        courierTrackingRef: courierTrackingRef.trim() || null,
        bagSealNumber: bagSealNumber.trim() || '',
        dispatchTemperature: dispatchTemp !== '' ? Number(dispatchTemp) : 4.0,
        notes: manifestNotes.trim() || null
      };

      const res = await b2bService.createManifest(payload);
      const manifestNum = res?.manifestNumber || res?.ManifestNumber || 'New Manifest';
      showToast(`Manifest ${manifestNum} generated successfully!`);
      setShowCreateModal(false);

      // Reset fields
      setCourierName('');
      setCourierTrackingRef('');
      setBagSealNumber('');
      setManifestNotes('');
      setDispatchTemp('4.0');

      await fetchManifests();

      // Automatically select newly created manifest if ID is returned
      const newId = res?.manifestID || res?.ManifestID;
      if (newId) {
        handleSelectManifest(newId);
      }
    } catch (err) {
      showToast(err.response?.data?.message || err.message || 'Error generating manifest.', 'error');
    } finally {
      setIsSubmittingCreate(false);
    }
  };

  // Pack Specimen Item Handler
  const handleAddItem = async (e) => {
    e.preventDefault();
    const barcode = scanBarcode.trim();
    const manifestId = activeHeader?.manifestID || activeHeader?.ManifestID;

    if (!barcode || !manifestId) return;

    setAddingItem(true);
    try {
      await b2bService.addManifestItem(manifestId, barcode);
      setScanBarcode('');
      showToast(`Barcode ${barcode} packed into manifest.`);
      await handleSelectManifest(manifestId);
      fetchManifests();
    } catch (err) {
      showToast(err.response?.data?.message || err.message || 'Error packing specimen.', 'error');
    } finally {
      setAddingItem(false);
    }
  };

  // Dispatch Confirmation Handler
  const handleConfirmDispatch = async () => {
    if (!dispatchDialogManifestId) return;
    setIsDispatching(true);
    try {
      await b2bService.dispatchManifest(dispatchDialogManifestId, {
        dispatchTemperature: Number(dispatchModalTemp) || 4.0
      });
      showToast('Manifest dispatched successfully with cold-chain lock.');
      setDispatchDialogManifestId(null);
      await handleSelectManifest(dispatchDialogManifestId);
      fetchManifests();
    } catch (err) {
      showToast(err.response?.data?.message || err.message || 'Error dispatching manifest.', 'error');
    } finally {
      setIsDispatching(false);
    }
  };

  // Receive Confirmation Handler
  const handleConfirmReceive = async () => {
    if (!receiveDialogManifestId) return;
    setIsReceiving(true);
    try {
      await b2bService.receiveManifest(receiveDialogManifestId, {
        arrivalTemperature: Number(receiveModalTemp) || 4.5,
        notes: receiveModalNotes.trim()
      });
      showToast('Manifest received and logged at destination processing lab.');
      setReceiveDialogManifestId(null);
      await handleSelectManifest(receiveDialogManifestId);
      fetchManifests();
    } catch (err) {
      showToast(err.response?.data?.message || err.message || 'Error receiving manifest.', 'error');
    } finally {
      setIsReceiving(false);
    }
  };

  // Verify Specimen Item (Accept / Reject)
  const handleVerifyItem = async (manifestItemId, status) => {
    const manifestId = activeHeader?.manifestID || activeHeader?.ManifestID;
    try {
      await b2bService.verifyManifestItem({
        manifestItemId,
        status,
        rejectionReason: status === 'REJECTED' ? 'Damaged / Hemolyzed / Leaked in Transit' : null
      });
      showToast(`Specimen marked as ${status}.`);
      if (manifestId) {
        handleSelectManifest(manifestId);
      }
    } catch (err) {
      showToast(err.response?.data?.message || err.message || 'Error verifying item.', 'error');
    }
  };

  // Print Manifest Slip Handler
  const handlePrintManifest = () => {
    if (!activeHeader) {
      showToast('Please select a manifest to print.', 'error');
      return;
    }
    window.print();
  };

  // Normalize selected manifest header and items across naming conventions
  const activeHeader = useMemo(() => {
    if (!selectedManifest) return null;
    return selectedManifest.header || selectedManifest.item1 || (selectedManifest.manifestID ? selectedManifest : null);
  }, [selectedManifest]);

  const activeItems = useMemo(() => {
    if (!selectedManifest) return [];
    return selectedManifest.items || selectedManifest.item2 || [];
  }, [selectedManifest]);

  const activeStatus = useMemo(() => {
    return (activeHeader?.status || activeHeader?.Status || '').toUpperCase();
  }, [activeHeader]);

  // Filtered manifests list
  const filteredManifests = useMemo(() => {
    return manifests.filter((m) => {
      const status = (m.status || m.Status || '').toUpperCase();
      const matchStatus =
        statusFilter === 'ALL'
          ? true
          : statusFilter === 'CREATED'
          ? status === 'CREATED' || status === 'DRAFT'
          : status === statusFilter;

      if (!matchStatus) return false;

      if (!searchQuery.trim()) return true;
      const q = searchQuery.toLowerCase();
      const num = (m.manifestNumber || m.ManifestNumber || '').toLowerCase();
      const src = (m.sourceCentreName || m.SourceCentreName || '').toLowerCase();
      const dst = (m.destinationCentreName || m.DestinationCentreName || '').toLowerCase();
      const courier = (m.courierName || m.CourierName || '').toLowerCase();
      const docket = (m.courierTrackingRef || m.CourierTrackingRef || '').toLowerCase();

      return num.includes(q) || src.includes(q) || dst.includes(q) || courier.includes(q) || docket.includes(q);
    });
  }, [manifests, searchQuery, statusFilter]);

  // KPI Calculations
  const kpis = useMemo(() => {
    const total = manifests.length;
    const inTransit = manifests.filter((m) => (m.status || m.Status || '').toUpperCase() === 'DISPATCHED').length;
    const received = manifests.filter((m) => (m.status || m.Status || '').toUpperCase() === 'RECEIVED').length;
    const pending = manifests.filter((m) => {
      const s = (m.status || m.Status || '').toUpperCase();
      return s === 'CREATED' || s === 'DRAFT';
    }).length;

    return { total, inTransit, received, pending };
  }, [manifests]);

  return (
    <div className="b2b-logistics-page">
      {/* Toast Notification Banner */}
      {toast && (
        <div className={`b2b-toast b2b-toast-${toast.type}`}>
          <span>{toast.message}</span>
          <button className="b2b-toast-close" onClick={() => setToast(null)}>&times;</button>
        </div>
      )}

      {/* Header Card */}
      <div className="b2b-logistics-header">
        <div>
          <h1 className="b2b-logistics-title">
            <span role="img" aria-label="truck">🚚</span> Inter-Centre Specimen Logistics &amp; Manifest Tracking
          </h1>
          <div className="b2b-logistics-subtitle">
            Secure chain-of-custody transfer manifests, barcode accession packing, and temperature-controlled cold-chain auditing.
          </div>
        </div>

        <div className="b2b-logistics-header-actions">
          <button
            type="button"
            className="b2b-btn b2b-btn-secondary"
            onClick={() => setShowGuide((prev) => !prev)}
            title="Toggle Workflow Guide"
          >
            {showGuide ? '📖 Hide Guide' : '📖 How it Works'}
          </button>
          <button
            type="button"
            className="b2b-btn b2b-btn-secondary"
            onClick={fetchManifests}
            title="Refresh Manifests"
          >
            &#x21bb; Refresh
          </button>
          <button
            type="button"
            className="b2b-btn b2b-btn-primary"
            onClick={() => setShowCreateModal(true)}
          >
            + Create Transfer Manifest
          </button>
        </div>
      </div>

      {/* Interactive Workflow Explainer Banner (Hinglish/English Guide) */}
      {showGuide && (
        <div className="b2b-logistics-guide-banner">
          <div className="b2b-guide-banner-header">
            <div className="b2b-guide-banner-title">
              <span role="img" aria-label="info">ℹ️</span> <strong>यह पेज किस काम आता है? (Specimen Transfer &amp; Chain-of-Custody Workflow)</strong>
            </div>
            <button
              type="button"
              className="b2b-guide-close-btn"
              onClick={() => setShowGuide(false)}
              title="Close Guide"
            >
              &times;
            </button>
          </div>
          <div className="b2b-guide-steps">
            <div className="b2b-guide-step">
              <div className="b2b-step-num">1</div>
              <div className="b2b-step-content">
                <strong>Manifest Banao (Challan)</strong>
                <p>Source Centre (जहाँ से सैंपल निकला) aur Destination Lab (जहाँ टेस्ट होगा) चुनें। Courier agent aur Bag Seal no दर्ज करें।</p>
              </div>
            </div>
            <div className="b2b-guide-step">
              <div className="b2b-step-num">2</div>
              <div className="b2b-step-content">
                <strong>Tubes Barcode Scan Karke Pack Karo</strong>
                <p>Bag में डाली जाने वाली हर tube का Barcode scan करें। Patient aur test details automatically jud jayengi।</p>
              </div>
            </div>
            <div className="b2b-guide-step">
              <div className="b2b-step-num">3</div>
              <div className="b2b-step-content">
                <strong>Dispatch &amp; Print Slip</strong>
                <p>Cold-chain temperature (2&deg;C-8&deg;C) record karke Dispatch karein aur Courier boy ko printed Manifest Challan dein।</p>
              </div>
            </div>
            <div className="b2b-guide-step">
              <div className="b2b-step-num">4</div>
              <div className="b2b-step-content">
                <strong>Central Lab Par Receive &amp; Verify</strong>
                <p>Main Lab पहुँचने पर Arrival Temp verify karein aur har tube scan karke Accept ya Reject karein।</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* KPI Summary Cards */}
      <div className="b2b-logistics-kpis">
        <div className="b2b-kpi-card">
          <div className="b2b-kpi-label">Total Manifests</div>
          <div className="b2b-kpi-value">{kpis.total}</div>
        </div>
        <div className="b2b-kpi-card" style={{ borderLeft: '4px solid #f59e0b' }}>
          <div className="b2b-kpi-label">Draft / Packing</div>
          <div className="b2b-kpi-value" style={{ color: '#d97706' }}>{kpis.pending}</div>
        </div>
        <div className="b2b-kpi-card" style={{ borderLeft: '4px solid #0284c7' }}>
          <div className="b2b-kpi-label">In Transit (Dispatched)</div>
          <div className="b2b-kpi-value" style={{ color: '#0284c7' }}>{kpis.inTransit}</div>
        </div>
        <div className="b2b-kpi-card" style={{ borderLeft: '4px solid #10b981' }}>
          <div className="b2b-kpi-label">Received at Lab</div>
          <div className="b2b-kpi-value" style={{ color: '#059669' }}>{kpis.received}</div>
        </div>
      </div>

      {/* Search & Filter Toolbar */}
      <div className="b2b-logistics-toolbar">
        <div className="b2b-search-box">
          <input
            type="text"
            className="b2b-form-input"
            placeholder="Search by Manifest No, Route, Courier or Docket..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        <div className="b2b-status-filters">
          {[
            { id: 'ALL', label: 'All Transfers' },
            { id: 'CREATED', label: 'Draft / Packing' },
            { id: 'DISPATCHED', label: 'In Transit' },
            { id: 'RECEIVED', label: 'Received' }
          ].map((f) => (
            <button
              key={f.id}
              type="button"
              className={`b2b-filter-pill ${statusFilter === f.id ? 'active' : ''}`}
              onClick={() => setStatusFilter(f.id)}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* Main Grid: Manifests List & Inspector */}
      <div className={`b2b-logistics-grid ${selectedManifest ? 'split' : 'single'}`}>
        {/* Left Column: Transfer Manifests List */}
        <div className="b2b-panel-card">
          <div className="b2b-panel-header">
            <h3 className="b2b-panel-title">
              Transfer Manifests ({filteredManifests.length})
            </h3>
            {selectedManifest && (
              <button
                type="button"
                className="b2b-btn b2b-btn-secondary b2b-btn-sm"
                onClick={() => setSelectedManifest(null)}
              >
                Close Inspector
              </button>
            )}
          </div>

          <div className="b2b-table-container">
            {loading ? (
              <div style={{ padding: '48px', textAlign: 'center', color: 'var(--text-muted)' }}>
                Loading transfer manifests...
              </div>
            ) : filteredManifests.length === 0 ? (
              <div style={{ padding: '48px', textAlign: 'center', color: 'var(--text-muted)' }}>
                {manifests.length === 0
                  ? 'No transfer manifests created yet. Click "+ Create Transfer Manifest" to start.'
                  : 'No transfer manifests match the selected filter.'}
              </div>
            ) : (
              <table className="b2b-logistics-table">
                <thead>
                  <tr>
                    <th>Manifest Number</th>
                    <th>Route</th>
                    <th>Specimens</th>
                    <th>Status</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredManifests.map((m) => {
                    const mId = m.manifestID || m.ManifestID;
                    const mNum = m.manifestNumber || m.ManifestNumber;
                    const isSelected = (activeHeader?.manifestID || activeHeader?.ManifestID) === mId;
                    const mStatus = (m.status || m.Status || 'CREATED').toUpperCase();
                    const srcName = m.sourceCentreName || m.SourceCentreName || `Centre ${m.sourceCentreID || m.SourceCentreID}`;
                    const dstName = m.destinationCentreName || m.DestinationCentreName || `Centre ${m.destinationCentreID || m.DestinationCentreID}`;
                    const dateStr = m.createdDate || m.CreatedDate;

                    return (
                      <tr
                        key={mId}
                        className={isSelected ? 'selected' : ''}
                        style={{ cursor: 'pointer' }}
                        onClick={() => handleSelectManifest(mId)}
                      >
                        <td>
                          <strong>{mNum}</strong>
                          <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '2px' }}>
                            {dateStr ? new Date(dateStr).toLocaleDateString('en-IN') : '—'}
                          </div>
                        </td>
                        <td>
                          <div style={{ fontWeight: 600 }}>{srcName}</div>
                          <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>&rarr; {dstName}</div>
                        </td>
                        <td>
                          <strong>{m.totalSpecimens ?? m.TotalSpecimens ?? 0}</strong> items
                        </td>
                        <td>
                          <span
                            className={`b2b-status-badge ${
                              mStatus === 'RECEIVED'
                                ? 'received'
                                : mStatus === 'DISPATCHED'
                                ? 'dispatched'
                                : 'created'
                            }`}
                          >
                            {mStatus}
                          </span>
                        </td>
                        <td>
                          <button
                            type="button"
                            className="b2b-btn b2b-btn-secondary b2b-btn-sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSelectManifest(mId);
                            }}
                          >
                            Inspect &rarr;
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {/* Right Column: Selected Manifest Details & Specimen Scanner */}
        {selectedManifest && (
          <div className="b2b-panel-card">
            <div className="b2b-panel-header">
              <div>
                <h3 className="b2b-panel-title">
                  Manifest: {activeHeader?.manifestNumber || activeHeader?.ManifestNumber || 'Details'}
                </h3>
                <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '2px' }}>
                  Carrier: <strong>{activeHeader?.courierName || activeHeader?.CourierName || 'Direct Courier'}</strong>
                  {activeHeader?.courierTrackingRef && ` (Docket: ${activeHeader.courierTrackingRef})`}
                </div>
              </div>

              {/* Action Buttons for Dispatch / Receive / Print */}
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                <button
                  type="button"
                  className="b2b-btn b2b-btn-secondary"
                  onClick={handlePrintManifest}
                  title="Print official manifest delivery challan"
                >
                  🖨️ Print Manifest Slip
                </button>

                {(activeStatus === 'CREATED' || activeStatus === 'DRAFT') && (
                  <button
                    type="button"
                    className="b2b-btn b2b-btn-primary"
                    onClick={() => {
                      setDispatchDialogManifestId(activeHeader?.manifestID || activeHeader?.ManifestID);
                      setDispatchModalTemp(String(activeHeader?.dispatchTemperature || activeHeader?.DispatchTemperature || '4.0'));
                    }}
                  >
                    🚀 Dispatch Manifest
                  </button>
                )}

                {activeStatus === 'DISPATCHED' && (
                  <button
                    type="button"
                    className="b2b-btn b2b-btn-success"
                    onClick={() => {
                      setReceiveDialogManifestId(activeHeader?.manifestID || activeHeader?.ManifestID);
                      setReceiveModalTemp('4.5');
                    }}
                  >
                    📥 Receive at Lab
                  </button>
                )}
              </div>
            </div>

            <div className="b2b-panel-body">
              {detailsLoading ? (
                <div style={{ padding: '36px', textAlign: 'center', color: 'var(--text-muted)' }}>
                  Loading details...
                </div>
              ) : (
                <>
                  {/* Metadata Summary Banner */}
                  <div className="b2b-manifest-meta">
                    <div className="b2b-meta-item">
                      <span className="b2b-meta-label">Current Status</span>
                      <span
                        className={`b2b-status-badge ${
                          activeStatus === 'RECEIVED'
                            ? 'received'
                            : activeStatus === 'DISPATCHED'
                            ? 'dispatched'
                            : 'created'
                        }`}
                        style={{ alignSelf: 'flex-start', marginTop: '3px' }}
                      >
                        {activeStatus}
                      </span>
                    </div>

                    <div className="b2b-meta-item">
                      <span className="b2b-meta-label">Route</span>
                      <span className="b2b-meta-val">
                        {activeHeader?.sourceCentreName || activeHeader?.SourceCentreName} &rarr;{' '}
                        {activeHeader?.destinationCentreName || activeHeader?.DestinationCentreName}
                      </span>
                    </div>

                    <div className="b2b-meta-item">
                      <span className="b2b-meta-label">Cold-Chain Temp</span>
                      <span className="b2b-meta-val">
                        {activeHeader?.dispatchTemperature ?? activeHeader?.DispatchTemperature ?? '4.0'} &deg;C
                        {activeHeader?.arrivalTemperature != null && ` (Arr: ${activeHeader.arrivalTemperature} °C)`}
                      </span>
                    </div>

                    <div className="b2b-meta-item">
                      <span className="b2b-meta-label">Security Bag Seal</span>
                      <span className="b2b-meta-val">
                        {activeHeader?.bagSealNumber || activeHeader?.BagSealNumber || 'N/A'}
                      </span>
                    </div>
                  </div>

                  {/* Specimen Packing Barcode Input (only when editable) */}
                  {(activeStatus === 'CREATED' || activeStatus === 'DRAFT') && (
                    <div className="b2b-scan-box">
                      <div style={{ fontSize: '12px', fontWeight: 700, color: '#166534', marginBottom: '8px' }}>
                        Scan &amp; Accession Specimen Barcode to Manifest
                      </div>
                      <form onSubmit={handleAddItem} className="b2b-scan-form">
                        <input
                          type="text"
                          className="b2b-form-input"
                          style={{ flex: 1 }}
                          placeholder="Scan or enter specimen barcode (e.g. BC-10023)..."
                          value={scanBarcode}
                          onChange={(e) => setScanBarcode(e.target.value)}
                          autoFocus
                          disabled={addingItem}
                        />
                        <button
                          type="submit"
                          className="b2b-btn b2b-btn-primary"
                          disabled={addingItem || !scanBarcode.trim()}
                        >
                          {addingItem ? 'Packing...' : '+ Pack Specimen'}
                        </button>
                      </form>
                    </div>
                  )}

                  {/* Packed Specimens List */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                    <h4 style={{ margin: 0, fontSize: '14px', fontWeight: 700 }}>
                      Packed Specimens ({activeItems.length})
                    </h4>
                  </div>

                  <div style={{ maxHeight: '420px', overflowY: 'auto' }} className="b2b-table-container">
                    {activeItems.length === 0 ? (
                      <div style={{ padding: '24px', textAlign: 'center', color: 'var(--text-muted)', fontSize: '13px' }}>
                        No specimens packed into this manifest yet.
                        {(activeStatus === 'CREATED' || activeStatus === 'DRAFT') && ' Scan a specimen barcode above.'}
                      </div>
                    ) : (
                      <table className="b2b-logistics-table">
                        <thead>
                          <tr>
                            <th>Barcode</th>
                            <th>Patient</th>
                            <th>Investigation</th>
                            <th>Sample Type</th>
                            <th>Status</th>
                            <th>Verification</th>
                          </tr>
                        </thead>
                        <tbody>
                          {activeItems.map((it, idx) => {
                            const itemId = it.manifestItemId || it.ManifestItemID || idx;
                            const itemBarcode = it.barcodeNo || it.BarcodeNo;
                            const itemPatient = it.patientName || it.PatientName;
                            const itemInvest = it.investigationName || it.InvestigationName;
                            const itemSample = it.sampleType || it.SampleType || 'Blood';
                            const itStatus = (it.status || it.Status || 'PACKED').toUpperCase();

                            return (
                              <tr key={itemId}>
                                <td>
                                  <code style={{ background: '#f1f5f9', padding: '2px 6px', borderRadius: '4px', fontWeight: 600 }}>
                                    {itemBarcode}
                                  </code>
                                </td>
                                <td>{itemPatient || '—'}</td>
                                <td>{itemInvest || '—'}</td>
                                <td>{itemSample}</td>
                                <td>
                                  <span
                                    className={`b2b-status-badge ${
                                      itStatus === 'ACCEPTED'
                                        ? 'accepted'
                                        : itStatus === 'REJECTED'
                                        ? 'rejected'
                                        : 'packed'
                                    }`}
                                  >
                                    {itStatus}
                                  </span>
                                </td>
                                <td>
                                  {activeStatus === 'RECEIVED' && itStatus === 'PACKED' && (
                                    <div style={{ display: 'flex', gap: '6px' }}>
                                      <button
                                        type="button"
                                        className="b2b-btn b2b-btn-success b2b-btn-sm"
                                        onClick={() => handleVerifyItem(it.manifestItemId || it.ManifestItemID, 'ACCEPTED')}
                                      >
                                        Accept
                                      </button>
                                      <button
                                        type="button"
                                        className="b2b-btn b2b-btn-warning b2b-btn-sm"
                                        style={{ background: '#ef4444', borderColor: '#ef4444' }}
                                        onClick={() => handleVerifyItem(it.manifestItemId || it.ManifestItemID, 'REJECTED')}
                                      >
                                        Reject
                                      </button>
                                    </div>
                                  )}
                                  {itStatus !== 'PACKED' && (
                                    <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>Verified</span>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>

      {/* ============================================================ */}
      {/* 1. Create Manifest Modal                                     */}
      {/* ============================================================ */}
      {showCreateModal && (
        <div className="b2b-modal-backdrop" onClick={() => !isSubmittingCreate && setShowCreateModal(false)}>
          <div className="b2b-modal-window" onClick={(e) => e.stopPropagation()}>
            <div className="b2b-modal-header">
              <h3 className="b2b-modal-title">Create Specimen Transfer Manifest</h3>
              <button
                type="button"
                className="b2b-modal-close-btn"
                onClick={() => setShowCreateModal(false)}
                disabled={isSubmittingCreate}
              >
                &times;
              </button>
            </div>

            <form onSubmit={handleCreateManifest}>
              <div className="b2b-modal-body">
                {/* Source and Destination Centres */}
                <div className="b2b-form-row">
                  <div className="b2b-form-group">
                    <label className="b2b-form-label">
                      Source Collection Centre (जहाँ से सैंपल निकल रहा है) <span className="b2b-required">*</span>
                    </label>
                    <select
                      className="b2b-form-select"
                      value={sourceCentreId}
                      onChange={(e) => setSourceCentreId(e.target.value)}
                      required
                    >
                      <option value="">-- Select Source Centre --</option>
                      {centres.map((c) => (
                        <option key={c.centreId} value={c.centreId}>
                          {c.centreName} {c.centreCode ? `(${c.centreCode})` : ''}
                        </option>
                      ))}
                    </select>
                    <span className="b2b-form-hint">Peripheral collection branch / clinic</span>
                  </div>

                  <div className="b2b-form-group">
                    <label className="b2b-form-label">
                      Destination Processing Lab (जहाँ टेस्ट होगा) <span className="b2b-required">*</span>
                    </label>
                    <select
                      className="b2b-form-select"
                      value={destinationCentreId}
                      onChange={(e) => setDestinationCentreId(e.target.value)}
                      required
                    >
                      <option value="">-- Select Destination Lab --</option>
                      {centres.map((c) => (
                        <option key={c.centreId} value={c.centreId}>
                          {c.centreName} {c.centreCode ? `(${c.centreCode})` : ''}
                        </option>
                      ))}
                    </select>
                    <span className="b2b-form-hint">Central laboratory / reference testing facility</span>
                  </div>
                </div>

                {/* Carrier and Tracking / Docket */}
                <div className="b2b-form-row">
                  <div className="b2b-form-group">
                    <label className="b2b-form-label">Carrier / Logistics Agent</label>
                    <input
                      type="text"
                      className="b2b-form-input"
                      placeholder="e.g. MedSpeed Couriers / Internal Runner"
                      value={courierName}
                      onChange={(e) => setCourierName(e.target.value)}
                    />
                  </div>

                  <div className="b2b-form-group">
                    <label className="b2b-form-label">Tracking / Docket Number</label>
                    <input
                      type="text"
                      className="b2b-form-input"
                      placeholder="e.g. DKT-998822"
                      value={courierTrackingRef}
                      onChange={(e) => setCourierTrackingRef(e.target.value)}
                    />
                  </div>
                </div>

                {/* Bag Seal & Temperature */}
                <div className="b2b-form-row">
                  <div className="b2b-form-group">
                    <label className="b2b-form-label">Security Bag Seal Number</label>
                    <input
                      type="text"
                      className="b2b-form-input"
                      placeholder="e.g. SEAL-88492"
                      value={bagSealNumber}
                      onChange={(e) => setBagSealNumber(e.target.value)}
                    />
                  </div>

                  <div className="b2b-form-group">
                    <label className="b2b-form-label">
                      Cold-Chain Temp (&deg;C) <span className="b2b-required">*</span>
                    </label>
                    <input
                      type="number"
                      step="0.1"
                      className="b2b-form-input"
                      value={dispatchTemp}
                      onChange={(e) => setDispatchTemp(e.target.value)}
                      required
                    />
                    <span className="b2b-form-hint">Standard refrigerated whole blood: 2.0&deg;C - 8.0&deg;C</span>
                  </div>
                </div>

                {/* Special Instructions / Notes */}
                <div className="b2b-form-group">
                  <label className="b2b-form-label">Logistics Instructions / Notes</label>
                  <textarea
                    rows={2}
                    className="b2b-form-textarea"
                    placeholder="e.g. Keep transport box upright, freeze gel packs included..."
                    value={manifestNotes}
                    onChange={(e) => setManifestNotes(e.target.value)}
                  />
                </div>
              </div>

              <div className="b2b-modal-footer">
                <button
                  type="button"
                  className="b2b-btn b2b-btn-secondary"
                  onClick={() => setShowCreateModal(false)}
                  disabled={isSubmittingCreate}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="b2b-btn b2b-btn-primary"
                  disabled={isSubmittingCreate}
                >
                  {isSubmittingCreate ? 'Generating...' : 'Generate Manifest Number'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ============================================================ */}
      {/* 2. Dispatch Confirmation Modal                                */}
      {/* ============================================================ */}
      {dispatchDialogManifestId && (
        <div className="b2b-modal-backdrop" onClick={() => !isDispatching && setDispatchDialogManifestId(null)}>
          <div className="b2b-modal-window" style={{ maxWidth: '440px' }} onClick={(e) => e.stopPropagation()}>
            <div className="b2b-modal-header">
              <h3 className="b2b-modal-title">Dispatch Transfer Manifest</h3>
              <button
                type="button"
                className="b2b-modal-close-btn"
                onClick={() => setDispatchDialogManifestId(null)}
              >
                &times;
              </button>
            </div>
            <div className="b2b-modal-body">
              <p style={{ margin: 0, fontSize: '13.5px', color: 'var(--text-secondary)' }}>
                Handing over specimens to the logistics agent. Confirm the departure cold-chain temperature:
              </p>
              <div className="b2b-form-group">
                <label className="b2b-form-label">
                  Departure Temperature (&deg;C) <span className="b2b-required">*</span>
                </label>
                <input
                  type="number"
                  step="0.1"
                  className="b2b-form-input"
                  value={dispatchModalTemp}
                  onChange={(e) => setDispatchModalTemp(e.target.value)}
                  required
                  autoFocus
                />
              </div>
            </div>
            <div className="b2b-modal-footer">
              <button
                type="button"
                className="b2b-btn b2b-btn-secondary"
                onClick={() => setDispatchDialogManifestId(null)}
                disabled={isDispatching}
              >
                Cancel
              </button>
              <button
                type="button"
                className="b2b-btn b2b-btn-primary"
                onClick={handleConfirmDispatch}
                disabled={isDispatching}
              >
                {isDispatching ? 'Dispatching...' : 'Confirm Dispatch'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ============================================================ */}
      {/* 3. Receive at Destination Lab Modal                           */}
      {/* ============================================================ */}
      {receiveDialogManifestId && (
        <div className="b2b-modal-backdrop" onClick={() => !isReceiving && setReceiveDialogManifestId(null)}>
          <div className="b2b-modal-window" style={{ maxWidth: '480px' }} onClick={(e) => e.stopPropagation()}>
            <div className="b2b-modal-header">
              <h3 className="b2b-modal-title">Receive Manifest at Lab</h3>
              <button
                type="button"
                className="b2b-modal-close-btn"
                onClick={() => setReceiveDialogManifestId(null)}
              >
                &times;
              </button>
            </div>
            <div className="b2b-modal-body">
              <p style={{ margin: 0, fontSize: '13.5px', color: 'var(--text-secondary)' }}>
                Record arrival temperature and intake condition at the central processing laboratory:
              </p>
              <div className="b2b-form-group">
                <label className="b2b-form-label">
                  Arrival Cold-Chain Temperature (&deg;C) <span className="b2b-required">*</span>
                </label>
                <input
                  type="number"
                  step="0.1"
                  className="b2b-form-input"
                  value={receiveModalTemp}
                  onChange={(e) => setReceiveModalTemp(e.target.value)}
                  required
                  autoFocus
                />
              </div>

              <div className="b2b-form-group">
                <label className="b2b-form-label">Intake Condition Notes</label>
                <textarea
                  rows={2}
                  className="b2b-form-textarea"
                  value={receiveModalNotes}
                  onChange={(e) => setReceiveModalNotes(e.target.value)}
                />
              </div>
            </div>
            <div className="b2b-modal-footer">
              <button
                type="button"
                className="b2b-btn b2b-btn-secondary"
                onClick={() => setReceiveDialogManifestId(null)}
                disabled={isReceiving}
              >
                Cancel
              </button>
              <button
                type="button"
                className="b2b-btn b2b-btn-success"
                onClick={handleConfirmReceive}
                disabled={isReceiving}
              >
                {isReceiving ? 'Receiving...' : 'Confirm Intake'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ============================================================ */}
      {/* 4. Dedicated Printable Manifest Slip (Visible in Print)       */}
      {/* ============================================================ */}
      {activeHeader && (
        <div className="b2b-manifest-print-sheet" aria-hidden="true">
          <div className="b2b-print-header">
            <div>
              <h2 className="b2b-print-title">INTELLISYS CLINICAL DIAGNOSTIC NETWORK</h2>
              <div className="b2b-print-subtitle">SPECIMEN TRANSFER MANIFEST &amp; CHAIN-OF-CUSTODY CHALLAN</div>
            </div>
            <div className="b2b-print-badge">
              <div className="b2b-print-barcode-text">*{activeHeader?.manifestNumber || activeHeader?.ManifestNumber}*</div>
              <div className="b2b-print-manifest-no">{activeHeader?.manifestNumber || activeHeader?.ManifestNumber}</div>
            </div>
          </div>

          <div className="b2b-print-route-grid">
            <div className="b2b-print-route-box">
              <div className="b2b-print-label">ORIGIN / SOURCE CENTRE</div>
              <div className="b2b-print-val">
                {activeHeader?.sourceCentreName || activeHeader?.SourceCentreName || `Centre #${activeHeader?.sourceCentreID || activeHeader?.SourceCentreID}`}
              </div>
            </div>
            <div className="b2b-print-route-arrow">&rarr;</div>
            <div className="b2b-print-route-box">
              <div className="b2b-print-label">DESTINATION PROCESSING LAB</div>
              <div className="b2b-print-val">
                {activeHeader?.destinationCentreName || activeHeader?.DestinationCentreName || `Centre #${activeHeader?.destinationCentreID || activeHeader?.DestinationCentreID}`}
              </div>
            </div>
          </div>

          <div className="b2b-print-meta-grid">
            <div><strong>Carrier / Agent:</strong> {activeHeader?.courierName || activeHeader?.CourierName || 'Direct Logistics Runner'}</div>
            <div><strong>Docket / Tracking:</strong> {activeHeader?.courierTrackingRef || activeHeader?.CourierTrackingRef || 'N/A'}</div>
            <div><strong>Bag Seal No:</strong> {activeHeader?.bagSealNumber || activeHeader?.BagSealNumber || 'N/A'}</div>
            <div><strong>Dispatch Temp:</strong> {activeHeader?.dispatchTemperature ?? activeHeader?.DispatchTemperature ?? 4.0} &deg;C</div>
            <div><strong>Generated Date:</strong> {activeHeader?.createdDate ? new Date(activeHeader.createdDate).toLocaleString('en-IN') : '—'}</div>
            <div><strong>Total Specimens:</strong> {activeItems.length} items</div>
          </div>

          <table className="b2b-print-table">
            <thead>
              <tr>
                <th style={{ width: '36px' }}>#</th>
                <th>Barcode</th>
                <th>Patient Details</th>
                <th>Investigation / Test</th>
                <th>Sample / Tube</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {activeItems.length === 0 ? (
                <tr>
                  <td colSpan={6} style={{ textAlign: 'center', padding: '16px' }}>
                    No specimens packed into this manifest.
                  </td>
                </tr>
              ) : (
                activeItems.map((it, idx) => (
                  <tr key={it.manifestItemId || it.ManifestItemID || idx}>
                    <td>{idx + 1}</td>
                    <td>
                      <strong className="b2b-print-code">{it.barcodeNo || it.BarcodeNo}</strong>
                    </td>
                    <td>{it.patientName || it.PatientName || '—'}</td>
                    <td>{it.investigationName || it.InvestigationName || '—'}</td>
                    <td>{it.sampleType || it.SampleType || 'Blood'}</td>
                    <td>{it.status || it.Status || 'PACKED'}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>

          <div className="b2b-print-signatures">
            <div className="b2b-print-sig-box">
              <div className="b2b-print-sig-line"></div>
              <div className="b2b-print-sig-title">Dispatched By (Source Phlebotomist)</div>
              <div className="b2b-print-sig-sub">Sign &amp; Timestamp</div>
            </div>
            <div className="b2b-print-sig-box">
              <div className="b2b-print-sig-line"></div>
              <div className="b2b-print-sig-title">Logistics Runner / Courier</div>
              <div className="b2b-print-sig-sub">Name, Phone &amp; Signature</div>
            </div>
            <div className="b2b-print-sig-box">
              <div className="b2b-print-sig-line"></div>
              <div className="b2b-print-sig-title">Received By (Destination Lab)</div>
              <div className="b2b-print-sig-sub">Intake Temp: _______&deg;C | Sign &amp; Date</div>
            </div>
          </div>

          <div className="b2b-print-footer">
            NABL ISO 15189 Compliant Specimen Chain-of-Custody Document &bull; Generated via InTeLISYS Diagnostics LIS
          </div>
        </div>
      )}
    </div>
  );
};

export default B2bSpecimenLogisticsPage;
